import { supabase, handleSupabaseError } from '@/lib/supabase';
import { Notification, UserNotification, NotificationCategory } from '@/types/notifications';
import { UserRole } from '@/types/enums';

/** Baris mentah dari view `user_notifications` (snake_case, sesuai migrasi 0035). */
interface UserNotificationRow {
  id: string;
  title: string;
  content: string;
  category: NotificationCategory;
  priority: string;
  action_url: string | null;
  created_at: string;
  expires_at: string | null;
  read_at: string | null;
  deleted_at: string | null;
  status: 'read' | 'deleted' | 'unread';
}

/**
 * Petakan baris view (snake_case) ke tipe UserNotification (camelCase).
 * Tanpa ini, field seperti `createdAt` selalu undefined → `new Date(undefined)`
 * di komponen menghasilkan Invalid Date → date-fns `format()` melempar
 * RangeError: Invalid time value.
 */
function mapUserNotification(row: UserNotificationRow): UserNotification {
  return {
    id: row.id,
    title: row.title,
    content: row.content,
    category: row.category,
    priority: row.priority as UserNotification['priority'],
    status: row.status,
    actionUrl: row.action_url,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
    readAt: row.read_at,
    deletedAt: row.deleted_at,
  };
}

/** Baris mentah dari tabel `notifications` (snake_case) untuk listing admin. */
interface SentNotificationRow {
  id: string;
  title: string;
  content: string;
  category: NotificationCategory;
  priority: string;
  status: 'pending' | 'sent' | 'failed' | 'archived';
  target_role: UserRole | null;
  target_custom_users: string[] | null;
  enable_push: boolean;
  enable_email: boolean;
  enable_sms: boolean;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
  users: { full_name: string } | { full_name: string }[] | null;
}

/** Notifikasi yang dibuat admin/pengelola — untuk halaman Notifikasi & Pengumuman. */
export interface SentNotification {
  id: string;
  title: string;
  content: string;
  category: NotificationCategory;
  priority: string;
  status: 'pending' | 'sent' | 'failed' | 'archived';
  targetRole: UserRole | null;
  targetCustomUsers: string[];
  enablePush: boolean;
  enableEmail: boolean;
  enableSms: boolean;
  expiresAt: string | null;
  createdAt: string;
  createdByName: string;
  totalRecipients: number;
  readCount: number;
}

function mapSentNotification(
  row: SentNotificationRow,
  stats?: { total: number; read: number },
): SentNotification {
  const creator = Array.isArray(row.users) ? row.users[0] : row.users;
  return {
    id: row.id,
    title: row.title,
    content: row.content,
    category: row.category,
    priority: row.priority,
    status: row.status,
    targetRole: row.target_role,
    targetCustomUsers: row.target_custom_users ?? [],
    enablePush: row.enable_push,
    enableEmail: row.enable_email,
    enableSms: row.enable_sms,
    expiresAt: row.expires_at,
    createdAt: row.created_at,
    createdByName: creator?.full_name ?? 'Admin',
    totalRecipients: stats?.total ?? 0,
    readCount: stats?.read ?? 0,
  };
}

class NotificationService {
  /**
   * Create a new notification for specific roles or users.
   * Only SUPER_ADMIN, PENGELOLA, ADMIN_BANK_SAMPAH can create.
   */
  async createNotification(payload: {
    title: string;
    content: string;
    category: NotificationCategory;
    targetRole?: UserRole | null;
    targetUsers?: string[];
    expiresAt?: string | null;
    enablePush?: boolean;
    enableEmail?: boolean;
    enableSms?: boolean;
    actionUrl?: string | null;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  }): Promise<{ data: Notification | null; error: string | null }> {
    try {
      const {
        title,
        content,
        category,
        targetRole,
        targetUsers,
        expiresAt,
        enablePush = true,
        enableEmail = false,
        enableSms = false,
        actionUrl,
        priority = 'medium',
      } = payload;

      // Call RPC function
      const { data, error } = await supabase.rpc('create_notification', {
        p_title: title,
        p_content: content,
        p_category: category,
        p_target_role: targetRole || null,
        p_target_users: targetUsers || null,
        p_expires_at: expiresAt || null,
        p_enable_push: enablePush,
        p_enable_email: enableEmail,
        p_enable_sms: enableSms,
      });

      if (error) throw error;

      // Fetch the created notification
      const { data: notif, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('id', data)
        .single();

      if (fetchError) throw fetchError;

      return {
        data: notif as Notification,
        error: null,
      };
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) };
    }
  }

  /**
   * Get all notifications for the current user
   */
  async getUserNotifications(): Promise<{
    data: UserNotification[];
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: (data as unknown as UserNotificationRow[]).map(mapUserNotification),
        error: null,
      };
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) };
    }
  }

  /**
   * Get unread notifications only
   */
  async getUnreadNotifications(): Promise<{
    data: UserNotification[];
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('status', 'unread')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: (data as unknown as UserNotificationRow[]).map(mapUserNotification),
        error: null,
      };
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) };
    }
  }

  /**
   * Get unread "Action Required" notifications count (for badge)
   */
  async getActionRequiredCount(): Promise<{
    data: number;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase.rpc('get_action_required_count');

      if (error) throw error;

      return {
        data: data as number,
        error: null,
      };
    } catch (error) {
      return { data: 0, error: handleSupabaseError(error) };
    }
  }

  /**
   * Mark a notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<{
    error: string | null;
  }> {
    try {
      const { error } = await supabase.rpc('mark_notification_read', {
        p_notification_id: notificationId,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Soft delete a notification (hide from user)
   */
  async deleteNotificationForUser(notificationId: string): Promise<{
    error: string | null;
  }> {
    try {
      const { error } = await supabase.rpc('delete_notification_for_user', {
        p_notification_id: notificationId,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Filter notifications by category
   */
  async getNotificationsByCategory(
    category: NotificationCategory,
  ): Promise<{ data: UserNotification[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: (data as unknown as UserNotificationRow[]).map(mapUserNotification),
        error: null,
      };
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) };
    }
  }

  /**
   * Get notifications by status
   */
  async getNotificationsByStatus(
    status: 'read' | 'deleted' | 'unread',
  ): Promise<{ data: UserNotification[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: (data as unknown as UserNotificationRow[]).map(mapUserNotification),
        error: null,
      };
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) };
    }
  }

  /**
   * Bulk mark notifications as read
   */
  async markMultipleAsRead(
    notificationIds: string[],
  ): Promise<{ error: string | null }> {
    try {
      const promises = notificationIds.map((id) =>
        supabase.rpc('mark_notification_read', {
          p_notification_id: id,
        }),
      );

      const results = await Promise.all(promises);
      const errors = results.filter((r) => r.error);

      if (errors.length > 0) {
        throw new Error(`Failed to mark ${errors.length} notification(s) as read`);
      }

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /** Notifikasi yang dibuat admin/pengelola, dengan statistik penerima & baca. */
  async getSentNotifications(): Promise<{ data: SentNotification[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          id, title, content, category, priority, status, target_role,
          target_custom_users, enable_push, enable_email, enable_sms,
          expires_at, created_at, created_by,
          users!notifications_created_by_fkey ( full_name )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const rows = (data ?? []) as unknown as SentNotificationRow[];
      const ids = rows.map((r) => r.id);

      // Statistik via RPC (SECURITY DEFINER) — RLS notification_recipients
      // hanya mengizinkan pengguna melihat baris miliknya sendiri, sehingga
      // agregasi langsung akan selalu kembalikan 0 untuk PENGELOLA/ADMIN.
      let statsMap = new Map<string, { total: number; read: number }>();
      if (ids.length > 0) {
        const { data: stats, error: statsError } = await supabase.rpc(
          'get_notification_recipient_stats',
          { p_notification_ids: ids },
        );
        if (!statsError && stats) {
          statsMap = new Map(
            (stats as Array<{ notification_id: string; total_recipients: number; read_count: number }>).map((s) => [
              s.notification_id,
              { total: Number(s.total_recipients), read: Number(s.read_count) },
            ]),
          );
        }
      }

      return {
        data: rows.map((row) => mapSentNotification(row, statsMap.get(row.id))),
        error: null,
      };
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) };
    }
  }

  /**
   * "Hapus" notifikasi dari sisi admin. Tabel `notifications` tidak punya
   * policy DELETE (audit trail dipertahankan) — sehingga ini mengarsipkan
   * (status='archived') lewat notif_update, bukan menghapus baris.
   */
  async archiveNotification(notificationId: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', notificationId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }
}

export const notificationService = new NotificationService();
export default notificationService;
